import { ProgramEntity } from "../../../../services/programs/programs.types";

/** Base-26 letter sequence (A, B, ... Z, AA, AB, ...) used as default group names. */
export const letterNames = (n: number): string[] => {
  const names: string[] = [];
  for (let i = 0; i < n; i++) {
    let index = i;
    let name = "";
    do {
      name = String.fromCharCode(65 + (index % 26)) + name;
      index = Math.floor(index / 26) - 1;
    } while (index >= 0);
    names.push(name);
  }
  return names;
};

export interface SubjectOption {
  value: number;
  label: string;
}

/**
 * Sentinel value for the "Todas" entry prepended to a grade's Materia
 * selector. Negative so it can never collide with a real program_id, and it
 * NEVER reaches component state or an emitted SelectionCell — it only travels
 * from the Select's onChange into resolveAllSubjectsToggle, which trades it
 * for the concrete program ids.
 */
export const ALL_SUBJECTS_VALUE = -1;

// GET /programs (findAll) returns nested grade/subject objects only — NO flat
// grade_id/subject_id — while other endpoints (e.g. getProgramById) do return
// them flat. Resolve both shapes rather than trusting the flat field alone.
const resolveGradeId = (program: ProgramEntity): number | undefined =>
  program.grade_id ?? program.grade?.id;

const resolveSubjectId = (program: ProgramEntity): number | undefined =>
  program.subject_id ?? program.subject?.id;

// Structural shapes for the generation delta — kept minimal so the util stays
// decoupled from SelectionCell/ClassEntity while accepting both.
export interface DeltaCell {
  grade_id: number;
  program_id: number;
  groups: string[];
}

export interface DeltaClass {
  grade_id: number;
  program_id: number;
  custom_id?: string | null;
  class_id?: string | null;
  deleted_at?: Date | string | null;
}

export interface GenerationDelta {
  newCount: number;
  existingCount: number;
}

const normalizeGroup = (name: string | null | undefined): string =>
  (name ?? "").trim().toLowerCase();

/**
 * Splits the grid selection (cells × shared groups) into combos that would
 * actually be created vs combos already covered by an ACTIVE class — the
 * client-side mirror of the backend resolveGrid new/exists split, used for
 * the honest pre-modal counter. Soft-deleted classes deliberately do NOT
 * count as existing: the backend reactivates them, so they are still a
 * change worth generating. Combos matching only a soft-deleted class show
 * as "new" here and as "reactivate" in the modal's authoritative preview.
 */
export const countGenerationDelta = (
  cells: DeltaCell[],
  existingClasses: DeltaClass[]
): GenerationDelta => {
  const activeKeys = new Set<string>();
  existingClasses.forEach((c) => {
    if (c.deleted_at) return;
    if (!c.grade_id || !c.program_id) return;
    const group = normalizeGroup(c.custom_id ?? c.class_id);
    if (!group) return;
    activeKeys.add(`${c.grade_id}|${c.program_id}|${group}`);
  });

  let newCount = 0;
  let existingCount = 0;
  cells.forEach((cell) => {
    cell.groups.forEach((groupName) => {
      const key = `${cell.grade_id}|${cell.program_id}|${normalizeGroup(groupName)}`;
      if (activeKeys.has(key)) {
        existingCount++;
      } else {
        newCount++;
      }
    });
  });

  return { newCount, existingCount };
};

/**
 * Narrows the selectable programs to the ones the school is actually licensed
 * for (SchoolToProgram). A class can only exist for a program the school
 * bought, so offering the full catalogue let operators generate classes for
 * subjects the school does not have.
 *
 * An EMPTY `schoolProgramIds` means "no licensing on record yet" — a school
 * being created — and deliberately restricts nothing: that first pass is
 * exactly what bootstraps the school's licensing (see computeLicensing on the
 * backend, which adds whatever the generated cells reference).
 */
export const restrictToSchoolPrograms = (
  programs: ProgramEntity[],
  schoolProgramIds: number[]
): ProgramEntity[] => {
  if (schoolProgramIds.length === 0) return programs;
  const licensed = new Set(schoolProgramIds);
  return programs.filter((program) => licensed.has(program.id));
};

/**
 * Groups a grade's programs by subject_id. When a subject has more than one
 * program (ambiguous), each option is disambiguated with the program title.
 * The option value is ALWAYS the program_id — the grade+subject combination
 * never gets resolved to a program implicitly.
 */
export const buildSubjectOptions = (
  programs: ProgramEntity[],
  gradeId: number,
  excludeSubjectIds: number[] = []
): SubjectOption[] => {
  const excluded = new Set(excludeSubjectIds);
  const gradePrograms = programs.filter((p) => resolveGradeId(p) === gradeId);

  const bySubject = new Map<number, ProgramEntity[]>();
  gradePrograms.forEach((program) => {
    const subjectId = resolveSubjectId(program);
    if (subjectId === undefined || excluded.has(subjectId)) return;
    const list = bySubject.get(subjectId) ?? [];
    list.push(program);
    bySubject.set(subjectId, list);
  });

  const options: SubjectOption[] = [];
  bySubject.forEach((subjectPrograms) => {
    const isAmbiguous = subjectPrograms.length > 1;
    subjectPrograms.forEach((program) => {
      options.push({
        value: program.id,
        label: isAmbiguous
          ? `${program.subject.name} — ${program.title}`
          : program.subject.name,
      });
    });
  });

  return options;
};

/** Every grade starts with exactly one group (SCRUM-673). */
export const DEFAULT_GROUP_NAME = "A";

const normalizeName = (name: string | null | undefined): string =>
  (name ?? "").trim();

const normalizeKey = (name: string | null | undefined): string =>
  normalizeName(name).toLowerCase();

/**
 * Default name for a group being added to a grade, in two steps.
 *
 * 1. REUSE: `schoolGroups` — the school-wide SchoolToGroup catalogue — is the
 *    set of names the school actually uses, so the first catalogue name this
 *    grade does not hold yet wins, in the catalogue's own order and spelling.
 *    Custom names ("Plata", "A1") are valid suggestions here. Suggesting from
 *    the grade alone offered names absent from the catalogue, which a
 *    generation then seeded into it as letters nobody asked for (SCRUM-915).
 * 2. FALL BACK: only once the grade holds every catalogue name (or no
 *    catalogue was given), pick the first FREE letter, so deleting "B" and
 *    adding again reuses B instead of leaving a gap at C. In THIS branch alone
 *    custom names occupy a slot without consuming a letter.
 */
export const nextGroupName = (
  existing: string[],
  schoolGroups: string[] = []
): string => {
  const inGrade = new Set(
    existing.map(normalizeKey).filter((n) => n !== "")
  );
  const unused = schoolGroups.find((name) => {
    const key = normalizeKey(name);
    return key !== "" && !inGrade.has(key);
  });
  if (unused !== undefined) return unused;

  const taken = new Set(
    [...existing, ...schoolGroups].map(normalizeKey).filter((n) => n !== "")
  );
  // taken.size + 1 candidates against at most taken.size collisions — by
  // pigeonhole at least one is always free.
  const candidates = letterNames(taken.size + 1);
  return (
    candidates.find((name) => !taken.has(name.toLowerCase())) ??
    DEFAULT_GROUP_NAME
  );
};

/**
 * Groups a grade's ALREADY-CREATED classes carry. These slots are locked in
 * the UI: group persistence is additive-only, so renaming one here would not
 * rename it server-side — it would mint a NEW group and generate a second set
 * of classrooms, orphaning the originals.
 *
 * Soft-deleted classes deliberately do not lock anything: the backend
 * reactivates those, so the group is still freely editable.
 */
export const lockedGroupsForGrade = (
  gradeId: number,
  existingClasses: DeltaClass[]
): string[] => {
  const seen = new Set<string>();
  const locked: string[] = [];
  existingClasses.forEach((c) => {
    if (c.deleted_at) return;
    if (c.grade_id !== gradeId) return;
    const name = normalizeName(c.custom_id ?? c.class_id);
    if (name === "") return;
    const key = name.toLowerCase();
    if (seen.has(key)) return;
    seen.add(key);
    locked.push(name);
  });
  return locked;
};

/**
 * Flattens the per-grade groups into the school-level list. Groups are
 * per-grade for CLASS GENERATION, but SchoolToGroup stays a school-wide
 * catalogue (the one GroupsTab shows and GenerarClasesModal persists
 * additively), so the school's list is the union of what the grades use.
 * Deduped case-insensitively — the backend persists case-preserving, so two
 * spellings would otherwise become two catalogue entries for one group.
 */
export const unionGroupNames = (
  groupsByGrade: Record<number, string[]>,
  alreadyPersisted: string[] = []
): string[] => {
  const seen = new Set<string>();
  const union: string[] = [];
  const absorb = (names: string[]) =>
    names.forEach((raw) => {
      const name = normalizeName(raw);
      if (name === "") return;
      const key = name.toLowerCase();
      if (seen.has(key)) return;
      seen.add(key);
      union.push(name);
    });

  // Persisted names go first so their spelling wins a case clash: the backend
  // stores groups case-preserving, and GroupsTab renders this same list.
  absorb(alreadyPersisted);
  Object.values(groupsByGrade).forEach(absorb);
  return union;
};

/** True when every offered program of a grade is currently selected. */
export const areAllSubjectsSelected = (
  allProgramIds: number[],
  selectedProgramIds: number[]
): boolean =>
  allProgramIds.length > 0 &&
  allProgramIds.every((id) => selectedProgramIds.includes(id));

/**
 * Resolves what a grade's Materia selection becomes after an onChange.
 *
 * The "Todas" sentinel is never stored, so its presence in `values` always
 * means the user just clicked that row — and the click toggles against the
 * PREVIOUS selection: all selected => clear, otherwise => select every
 * offered program. Any change without the sentinel (including deselecting a
 * single materia while all were selected, which drops "Todas" back to
 * unchecked on its own) passes through untouched.
 */
export const resolveAllSubjectsToggle = (
  values: number[],
  allProgramIds: number[],
  selectedProgramIds: number[]
): number[] => {
  if (!values.includes(ALL_SUBJECTS_VALUE)) return values;
  return areAllSubjectsSelected(allProgramIds, selectedProgramIds)
    ? []
    : [...allProgramIds];
};
